import React, { useState, useEffect, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { FunnelIcon, PlusIcon } from '@heroicons/react/24/outline';
import { MatchListItem, getMatchOutcome } from '../types/match';
import { Event, EventType } from '../types/event';
import { matchesAPI, eventsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isStaff } from '../types/auth';
import { useBatchedList } from '../hooks/useBatchedList';
import AgeGroupSelect from '../components/common/AgeGroupSelect';
import MatchDetailView from '../components/match/MatchDetailView';
import EventForm from '../components/events/EventForm';
import FilterAccordion from '../components/ui/FilterAccordion';
import LazyLoadTrigger from '../components/ui/LazyLoadTrigger';

const Matches: React.FC = () => {
  const { user } = useAuth();
  usePageTitle('Mérkőzések');
  const [matches, setMatches] = useState<MatchListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedAgeGroupId, setSelectedAgeGroupId] = useState<number | null>(null);
  const [showPast, setShowPast] = useState(true);

  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isMatchDetailOpen, setIsMatchDetailOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  const canEdit = isStaff(user);
  const { visible: visibleMatches, hasMore, loadMore } = useBatchedList(matches, 5);

  const loadMatches = useCallback(async () => {
    setLoading(true);
    try {
      const params: { ageGroupId?: number; dateFrom?: string } = {};
      if (selectedAgeGroupId) params.ageGroupId = selectedAgeGroupId;
      if (!showPast) {
        // A múltbeli meccsek elrejtése: a mai nap elejétől, hogy a ma futó meccs is látsszon
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        params.dateFrom = today.toISOString();
      }
      const data = await matchesAPI.getAll(params);
      setMatches(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedAgeGroupId, showPast]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  const handleOpenMatch = async (m: MatchListItem) => {
    try {
      const ev = await eventsAPI.getById(m.eventId);
      setSelectedEvent(ev);
      setIsMatchDetailOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateMatch = () => {
    setSelectedEvent(null);
    setIsFormModalOpen(true);
  };

  const handleEventSaved = () => {
    setIsFormModalOpen(false);
    loadMatches();
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const getResultDisplay = (m: MatchListItem) => {
    if (m.homeScore == null && m.awayScore == null) {
      // A már lejátszott, de eredmény nélküli meccs jól látszódjon (adatpótlás)
      return new Date(m.startTime) < new Date()
        ? <span className="inline-block rounded bg-amber-100 px-1.5 py-px text-[11px] font-medium text-amber-800">Eredmény hiányzik</span>
        : '–';
    }
    return `${m.homeScore ?? '?'} – ${m.awayScore ?? '?'}`;
  };

  // A mi szemszögünkből színez; ha nem tudni, hazai-e a meccs, semleges marad
  const getResultClass = (m: MatchListItem) => {
    switch (getMatchOutcome(m)) {
      case 'win':
        return 'text-green-600 font-semibold';
      case 'loss':
        return 'text-red-600 font-semibold';
      case 'draw':
        return 'text-yellow-600 font-semibold';
      default:
        return m.homeScore != null && m.awayScore != null ? 'text-gray-900 font-semibold' : 'text-gray-500';
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mérkőzések</h1>
          <p className="hidden sm:block text-sm text-gray-500 mt-1">Az összes mérkőzés áttekintése</p>
        </div>
        {canEdit && (
          <button
            onClick={handleCreateMatch}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <PlusIcon className="h-5 w-5" />
            Új mérkőzés
          </button>
        )}
      </div>

      <div className="mb-6">
        <FilterAccordion
          title="Szűrők"
          activeCount={(selectedAgeGroupId ? 1 : 0) + (showPast ? 0 : 1)}
        >
          <div className="flex flex-col gap-3 p-3 bg-white rounded-lg shadow-sm md:flex-row md:flex-wrap md:items-center">
            <FunnelIcon className="hidden md:block h-5 w-5 text-gray-400" />
            <AgeGroupSelect
              value={selectedAgeGroupId}
              onChange={setSelectedAgeGroupId}
              placeholder="Összes korosztály"
            />
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showPast}
                onChange={(e) => setShowPast(e.target.checked)}
                className="rounded border-gray-300 text-primary-600"
              />
              Múltbeli meccsek
            </label>
          </div>
        </FilterAccordion>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : matches.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Nincs mérkőzés.</div>
      ) : (
        <>
          {/* Mobil kártyalista (md alatt) */}
          <div className="md:hidden grid grid-cols-2 gap-3">
            {visibleMatches.map((m) => (
              <div
                key={m.eventId}
                onClick={() => handleOpenMatch(m)}
                className="bg-white rounded-lg shadow border p-3 flex flex-col cursor-pointer hover:bg-gray-50"
              >
                <div className="flex items-start justify-between gap-1 mb-1.5">
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-gray-900 break-words hyphens-auto leading-snug">
                      {m.opponentName ?? <span className="font-normal italic text-gray-400">nincs megadva</span>}
                      {m.isFutsal && (
                        <span className="ml-1.5 inline-block rounded bg-orange-100 px-1.5 py-px align-middle text-[11px] font-medium text-orange-800">Futsal</span>
                      )}
                    </div>
                    {m.eventTitle && (
                      <div className="text-xs text-gray-500 truncate">{m.eventTitle}</div>
                    )}
                  </div>
                  <span className="shrink-0">
                    {m.isHomeMatch == null ? (
                      <span className="text-xs text-gray-400">–</span>
                    ) : m.isHomeMatch ? (
                      <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">Hazai</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">Idegen</span>
                    )}
                  </span>
                </div>
                <div className="flex-1 space-y-0.5 text-xs text-gray-600">
                  <div className={`text-sm ${getResultClass(m)}`}>{getResultDisplay(m)}</div>
                  <div className="truncate">
                    <span className="text-gray-400">Dátum: </span>
                    {formatDate(m.startTime)}
                  </div>
                  <div className="truncate">
                    <span className="text-gray-400">Korosztály: </span>
                    {m.ageGroup ?? '–'}
                  </div>
                </div>
              </div>
            ))}
            {hasMore && (
              <div className="col-span-2">
                <LazyLoadTrigger key={visibleMatches.length} onLoadMore={loadMore} />
              </div>
            )}
          </div>

          {/* Desktop táblázat (md-től) */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dátum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Esemény</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ellenfél</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Korosztály</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">H/I</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Eredmény</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {matches.map((m) => (
                  <tr
                    key={m.eventId}
                    onClick={() => handleOpenMatch(m)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDate(m.startTime)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{m.eventTitle ?? '–'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {m.opponentName ?? <span className="font-normal italic text-gray-400">nincs megadva</span>}
                      {m.isFutsal && (
                        <span className="ml-1.5 inline-block rounded bg-orange-100 px-1.5 py-px text-[11px] font-medium text-orange-800">Futsal</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{m.ageGroup ?? '–'}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      {m.isHomeMatch == null ? '–' : m.isHomeMatch ? (
                        <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">H</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">I</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-sm text-center ${getResultClass(m)}`}>
                      {getResultDisplay(m)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}

      <MatchDetailView
        event={selectedEvent}
        isOpen={isMatchDetailOpen}
        onClose={() => {
          setIsMatchDetailOpen(false);
          setSelectedEvent(null);
          loadMatches();
        }}
      />

      <EventForm
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        event={null}
        onSave={handleEventSaved}
        defaultType={EventType.Match}
      />
    </div>
  );
};

export default Matches;
